import { useEffect, useState } from 'react'
import { Mtds, NoPosFoundError } from 'prove-common'
import { AccurateLocation } from '../accurate-location/AccurateLocation'
import { NotAccurateLocation } from '../not-accurate-location/NotAccurateLocation'
import { SavedLocationCard } from '../saved-location-card/SavedLocationCard'

type LocationFinderCardProps = {
  position?: GeolocationPosition
  locationRequired: boolean
  sampleId?: string
  newSample?: boolean
  isExistingSample?: boolean
  onSelectedLonLatChanged?: (lonLat: [number, number]) => void
  deferSave?: boolean
}

export const ACCEPTABLE_ACCURACY_METERS = 50

export const LocationFinderCard = ({
  position,
  locationRequired,
  sampleId,
  newSample = false,
  isExistingSample = false,
  onSelectedLonLatChanged,
  deferSave = false
}: LocationFinderCardProps) => {
  const [geolocationTimedOut, setGeolocationTimedOut] = useState(false)
  const acceptableAccuracy = ACCEPTABLE_ACCURACY_METERS

  useEffect(() => {
    if (isExistingSample) return

    let timerId: NodeJS.Timeout | undefined
    if (locationRequired && !position?.coords && !geolocationTimedOut) {
      timerId = setTimeout(() => {
        if (!position?.coords) {
          setGeolocationTimedOut(true)
        }
      }, 5000)
    } else if (position?.coords) {
      setGeolocationTimedOut(false)
    }

    return () => {
      if (timerId) {
        clearTimeout(timerId)
      }
    }
  }, [position, locationRequired, geolocationTimedOut, isExistingSample])

  if (!locationRequired) {
    return null
  }

  if (isExistingSample) {
    if (position) {
      return (
        <SavedLocationCard
          position={position}
          sampleId={sampleId}
          deferSave={deferSave}
          onSelectedLonLatChanged={onSelectedLonLatChanged}
        />
      )
    }
    return (
      <NoPosFoundError
        deferSave={deferSave}
        onSelectedLonLatChanged={onSelectedLonLatChanged}
      />
    )
  }

  let content: React.ReactNode
  if (position) {
    if (position.coords.accuracy > acceptableAccuracy) {
      content = (
        <NotAccurateLocation
          position={position}
          sampleId={sampleId}
          requiredAccuracy={acceptableAccuracy}
          newSample={newSample}
          onSelectedLonLatChanged={onSelectedLonLatChanged}
        />
      )
    } else {
      content = (
        <AccurateLocation
          position={position}
          sampleId={sampleId}
          newSample={newSample}
          onSelectedLonLatChanged={onSelectedLonLatChanged}
        />
      )
    }
  } else if (geolocationTimedOut) {
    content = (
      <NoPosFoundError
        newSample={newSample}
        onSelectedLonLatChanged={onSelectedLonLatChanged}
      />
    )
  } else {
    content = (
      <Mtds.Alert>
        <Mtds.Heading as='h4'>Henter posisjon...</Mtds.Heading>
        <p>Denne oppgaven krever nøyaktig posisjon</p>
        <Mtds.Spinner />
      </Mtds.Alert>
    )
  }

  return <>{content}</>
}

export default LocationFinderCard
